import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.from.js';
import 'core-js/modules/es.array.some.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.string.iterator.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { forwardRef, useMemo } from 'react';
import { useMenu, MenuProvider } from '@mui/base';
import classNames from 'classnames';
import { LoaderIcon } from '@cognite/cogs-icons';
import { getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import { Body } from '../../tokens/typography/typography.js';
import { CogsMenuContext } from './menu-context.js';
import Footer from './menu-footer.js';
import Header from './menu-header.js';
import { MenuItem } from './menu-item.js';
import MenuReorder from './menu-reorder.js';
import { MenuSection } from './menu-section.js';
import { MenuSubmenu } from './menu-submenu.js';

/**
 * Menu
 * =============================================================================
 * @deprecated This component will be replaced by the new `Menu` component made on top of floating-ui from lab in the future.
 * The Menu component renders a listbox and returns a compound component that includes
 * the `Menu.Item`.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-menu--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Menu>
 *   <Menu.Item>Option 1</Menu.Item>
 *   <Menu.Item>Option 2</Menu.Item>
 * </Menu>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-menu--base)
 */
var MenuBase = /*#__PURE__*/forwardRef(function (_a, ref) {
  var children = _a.children,
    loading = _a.loading,
    _a$loadingText = _a.loadingText,
    loadingText = _a$loadingText === undefined ? 'Loading results...' : _a$loadingText,
    className = _a.className,
    hasCheckbox = _a.hasCheckbox,
    hasSwitch = _a.hasSwitch,
    hasReorder = _a.hasReorder,
    inverted = _a.inverted,
    themeProps = _a.themeProps,
    _a$items = _a.items,
    items = _a$items === undefined ? [] : _a$items,
    _a$onReorder = _a.onReorder,
    onReorder = _a$onReorder === undefined ? function () {} : _a$onReorder,
    _a$open = _a.open,
    _open = _a$open === undefined ? false : _a$open,
    otherProps = __rest(_a, ["children", "loading", "loadingText", "className", "hasCheckbox", "hasSwitch", "hasReorder", "inverted", "themeProps", "items", "onReorder", "open"]);
  var _useMenu = useMenu({
      listboxRef: ref
    }),
    getListboxProps = _useMenu.getListboxProps,
    contextValue = _useMenu.contextValue;
  var extendedClassName = classNames.apply(undefined, ['cogs-v10', 'cogs-menu', loading && 'cogs-menu--loading'].concat(getThemeClassNames(inverted, themeProps), [className]));
  var _React$useState = React.useState(_open),
    open = _React$useState[0],
    setOpen = _React$useState[1];
  var menus = function menus() {
    if (loading) {
      return jsxs("div", {
        className: "cogs-v10 cogs-menu__loading-container",
        children: [jsx(IconWrapper, {
          size: 24,
          "data-testid": "cogs-menu-loading-icon",
          children: jsx(LoaderIcon, {})
        }), jsx(Body, {
          size: "medium",
          children: loadingText
        })]
      });
    }
    return children;
  };
  var menuContext = useMemo(function () {
    return {
      items: items,
      hasCheckbox: hasCheckbox,
      hasReorder: hasReorder,
      hasSwitch: hasSwitch,
      menuContext: contextValue,
      open: open
    };
  }, [contextValue, hasCheckbox, hasReorder, hasSwitch, items, open]);
  var listBoxProps = getListboxProps();
  return jsx(CogsMenuContext.Provider, {
    value: menuContext,
    children: jsx(MenuReorder, Object.assign({
      onReorder: onReorder,
      items: items,
      hasReorder: hasReorder,
      className: extendedClassName
    }, otherProps, listBoxProps, {
      // This makes it so the Menu becomes 'open' when you focus one of it's children,
      // without this, when we're interacting with the Menu we need to make sure
      // that it receives `open: true`, otherwise it will behave weirdly when
      // interacting with it with the keyboard.
      onFocus: function onFocus() {
        setOpen(true);
      },
      // Adding this doesn't allow you to leave the Menu if you the Menu.Item
      // has interactive children, e.g. action, checkbox, switch. Maybe it's
      // sufficient to just keep it like this, since the parent will be able
      // to control the visibility, as long as it destroys the component as well,
      // which means the state gets reset as well.
      // onBlur={() => {
      //   setOpen(false);
      // }}
      onKeyDown: function onKeyDown(e) {
        // menu has some built-in functionality e.g. pressing the first
        // letter of an item's text moves the focus to that item. this
        // happens by preventing the default behavior which causes an issue
        // if an item has a form element e.g. input or textarea, because
        // preventing default behaviour, means not being able to type in
        // that form element. this condition checks if it is the menu itself
        // or its direct child (menu item) that originated the event or
        // did this event bubble from a deeper child that might need the
        // onKeyDown to work in a default manner, in which case we don't
        // run the menu's onKeyDown
        var shouldCallMenuKeyDown = e.target === e.currentTarget || Array.from(e.currentTarget.children).some(function (child) {
          return child === e.target;
        });
        if (shouldCallMenuKeyDown) {
          e.preventDefault();
          listBoxProps.onKeyDown(e);
        }
      },
      children: jsx(MenuProvider, {
        value: contextValue,
        children: menus()
      })
    }))
  });
});
var Menu = Object.assign({}, MenuBase, {
  Header: Header,
  Footer: Footer,
  Item: MenuItem,
  Submenu: MenuSubmenu,
  Section: MenuSection
});

export { Menu };
